import { FC } from 'react';
import FormattedPrice from '@/components/common/formatted-price/formatted-price';
import { Link } from '@/components/ui';
import LazyNavImage from '@/components/ui/image/lazy-nav-image';
import useCoreConfig from '@/hooks/use-core-config';
import { useGetDiscountType } from '@/hooks/use-has-discount';
import { useKeycloak } from '@/providers/keycloak-provider';
import { cn } from '@/utils';
import { getCategoryId } from '@/utils/get-category-id';
import { PRODUCT_CARD_IMAGE_PARAMS } from '@/utils/product-listing-lcp-preload';
import { selectMainImage } from '@/utils/select-main-image';

interface MiniProductCardProps {
  image: string;
  name: string;
  metaUrl: string;
  price: number;
  discountedPrice: number;
  locale: string;
  superCategories?: string[];
  productId?: string;
}

const MiniProductCard: FC<MiniProductCardProps> = ({
  image,
  name,
  metaUrl,
  price,
  discountedPrice,
  locale,
  superCategories,
  productId,
}) => {
  const { keycloak } = useKeycloak();
  const { features } = useCoreConfig();
  const isAuthenticated = keycloak?.authenticated;
  const shouldShowPrice = isAuthenticated || features.priceForGuests;
  const parentCategoryId = getCategoryId(superCategories);
  const { getDiscountType } = useGetDiscountType();

  return (
    <Link
      withoutLocale
      href={`/${locale}/product/${metaUrl}`}
      className="hover:bg-default-200 flex items-start rounded-lg p-4 transition-colors"
    >
      <div className="aspect-square w-full overflow-hidden rounded-md border border-gray-200 shadow-md">
        <LazyNavImage src={selectMainImage([image], PRODUCT_CARD_IMAGE_PARAMS)} alt={name} />
      </div>
      <div className="mt-2">
        <div className="mt-2">
          <span className="line-clamp-3 block max-w-[150px] text-sm font-medium text-neutral-900">
            {name}
          </span>
          <div
            className={cn('mt-2 flex max-w-[150px] flex-col items-start gap-1', {
              hidden: !shouldShowPrice,
              'mt-0': !shouldShowPrice,
            })}
          >
            {(() => {
              const discountType = getDiscountType(
                { price, discountedPrice },
                parentCategoryId,
                productId,
              );

              if (discountType === 'NONE') {
                return (
                  <>
                    <FormattedPrice
                      price={price}
                      size="sm"
                      fontWeight="bold"
                      color="gray"
                      productId={productId}
                      showVatSuffix
                    />
                    <span className="h-4" />
                  </>
                );
              }

              if (discountType === 'PRODUCT') {
                return (
                  <>
                    <FormattedPrice
                      lineThrough
                      price={price}
                      size="xs"
                      fontWeight="bold"
                      color="gray"
                      productId={productId}
                    />
                    <FormattedPrice
                      price={discountedPrice}
                      size="sm"
                      fontWeight="bold"
                      color="red"
                      productId={productId}
                      showVatSuffix
                    />
                  </>
                );
              }

              if (discountType === 'PRICE_LIST') {
                // Same shape as the PRODUCT branch but `FormattedPrice`
                // pulls the discounted/list values from the matched
                // price-list entry rather than the catalog product.
                return (
                  <>
                    <FormattedPrice
                      lineThrough
                      price={price}
                      size="xs"
                      fontWeight="bold"
                      color="gray"
                      productId={productId}
                    />
                    <FormattedPrice
                      price={price}
                      size="sm"
                      fontWeight="bold"
                      color="red"
                      productId={productId}
                      showVatSuffix
                    />
                  </>
                );
              }

              // COMPANY discount
              return (
                <>
                  <FormattedPrice
                    lineThrough
                    price={price}
                    size="xs"
                    fontWeight="bold"
                    color="gray"
                    productId={productId}
                  />
                  <FormattedPrice
                    price={price}
                    size="sm"
                    fontWeight="bold"
                    color="red"
                    categoryId={parentCategoryId}
                    productId={productId}
                    showVatSuffix
                  />
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default MiniProductCard;
